import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';

export const commentKeys = {
  all: ['comments'] as const,
  list: (p: api.CommentListParams) => [...commentKeys.all, 'list', p] as const,
  contentType: (modelLabel: string) => ['content-type', modelLabel] as const,
};

/** Cache the model→ContentType id mapping aggressively — it never changes per deploy. */
export function useContentType(modelLabel: string) {
  return useQuery({
    queryKey: commentKeys.contentType(modelLabel),
    queryFn: () => api.resolveContentType(modelLabel),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Comments per request when loading a thread. */
const THREAD_PAGE_SIZE = 100;
/** Most pages one thread loads (2,000 comments); beyond that `truncated` is set. */
const THREAD_MAX_PAGES = 20;

export interface CommentThread {
  results: api.Comment[];
  count: number;
  /** More comments exist than were loaded (past `THREAD_MAX_PAGES`). */
  truncated: boolean;
}

/** The whole thread, page by page — a panel that showed only the first page
 *  cut long threads off with no sign of it. */
async function listThread(params: api.CommentListParams): Promise<CommentThread> {
  const results: api.Comment[] = [];
  for (let page = 1; ; page += 1) {
    const data = await api.listComments({ ...params, page, page_size: THREAD_PAGE_SIZE });
    results.push(...data.results);
    if (!data.next || page >= THREAD_MAX_PAGES) {
      return { results, count: data.count, truncated: Boolean(data.next) };
    }
  }
}

export function useComments(params: api.CommentListParams) {
  return useQuery({
    queryKey: commentKeys.list(params),
    queryFn: () => listThread(params),
    // Comments must be scoped to a target — `enabled` guards against missing ids.
    enabled: Boolean(params.content_type && params.object_id),
  });
}

export function useCreateComment(params: api.CommentListParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: api.CommentInput) => api.createComment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commentKeys.list(params) });
      toast.success('Comment posted');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateComment(params: api.CommentListParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => api.updateComment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentKeys.list(params) }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function usePinComment(params: api.CommentListParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.pinComment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentKeys.list(params) }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteComment(params: api.CommentListParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteComment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentKeys.list(params) }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
